import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Search, ChevronDown, Pin, Pencil, Plus, Eye, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../components/ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS = ['Tümü', 'Birimler', 'Duyurular', 'Olay', 'Wiki'];

function Badge({ children, variant = 'default' }) {
  const map = {
    edited: 'bg-amber-100 text-amber-800 border border-amber-200',
    pinned: 'bg-rose-100 text-rose-800 border border-rose-200',
    role: 'bg-rose-50 text-rose-700 border border-rose-100',
    default: 'bg-zinc-100 text-zinc-700 border border-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-700',
  };
  return <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md inline-flex items-center gap-1 ${map[variant]}`}>{children}</span>;
}

export default function Wiki() {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('Tümü');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const params = {};
    if (q) params.q = q;
    if (cat !== 'Tümü') params.category = cat;
    setLoading(true);
    const t = setTimeout(() => {
      axios.get(`${API}/wiki`, { params })
        .then(r => setItems(r.data || []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q, cat]);

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)' }}>
      <Header />
      <main className="max-w-4xl mx-auto px-5 lg:px-8 pt-14 pb-24">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl lg:text-5xl font-semibold tracking-tight">Wiki</h1>
            <p className="mt-3" style={{ color: 'var(--text-muted)' }}>Gruplarımız hakkındaki bilgileri burada bulabilirsiniz.</p>
          </div>
          {user?.canEdit && (
            <Link to="/wiki/new" className="pill-btn text-sm" style={{ background: 'var(--text)', color: 'var(--bg)', borderColor: 'transparent' }}>
              <Plus size={14} /> Yeni Yazı
            </Link>
          )}
        </div>

        <div className="mt-10 card-soft rounded-2xl p-2 flex items-stretch gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
            <input value={q} onChange={e => setQ(e.target.value)} type="text" placeholder="Başlığa göre ara" className="w-full pl-11 pr-4 h-12 bg-transparent rounded-xl outline-none text-sm" style={{ color: 'var(--text)' }} />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="h-12 px-4 inline-flex items-center gap-2 rounded-xl text-sm font-semibold uppercase tracking-wider whitespace-nowrap hover:opacity-80" style={{ color: 'var(--text-muted)' }}>
                {cat}
                <ChevronDown size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {CATS.map(c => (
                <DropdownMenuItem key={c} onClick={() => setCat(c)} className="cursor-pointer">{c}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="mt-8 flex flex-col gap-4">
          {loading && Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card-soft rounded-2xl h-40 animate-pulse" />
          ))}
          {!loading && items.map(a => (
            <Link to={`/wiki/${a.slug}`} key={a.slug} className="card-soft rounded-2xl overflow-hidden hover:-translate-y-0.5 transition-transform duration-300 block">
              {a.image && (
                <div className="aspect-[21/7] overflow-hidden" style={{ background: 'var(--pill-hover)' }}>
                  <img src={a.image} alt={a.title} className="w-full h-full object-cover" loading="lazy" />
                </div>
              )}
              <div className="p-5 sm:p-6">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-[17px] font-semibold">{a.title}</h3>
                  {a.edited && <Badge variant="edited"><Pencil size={10} /> Düzenlendi</Badge>}
                  {a.pinned && <Badge variant="pinned"><Pin size={10} /> Sabit</Badge>}
                </div>
                {a.excerpt && <p className="mt-2 text-sm" style={{ color: 'var(--text-muted)' }}>{a.excerpt}</p>}
                <div className="mt-4 flex items-center gap-2 text-xs flex-wrap" style={{ color: 'var(--text-muted)' }}>
                  <span className="font-semibold" style={{ color: 'var(--text)' }}>{a.author}</span>
                  <Badge variant="role">{a.role}</Badge>
                  <span>— {a.date}</span>
                  {a.category && <span>• {a.category}</span>}
                  <span className="inline-flex items-center gap-1 ml-auto">
                    <Eye size={11} /> {(a.views || 0).toLocaleString('tr-TR')}
                  </span>
                </div>
              </div>
            </Link>
          ))}
          {!loading && items.length === 0 && (
            <div className="card-soft rounded-2xl p-10 text-center" style={{ color: 'var(--text-muted)' }}>Henüz yazı yok.</div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
